import { Alert, Calendar, DatePicker, Form, Input, Modal, Select } from "antd";
import { useContext, useEffect, useState } from "react";
import { AuthenticationContext } from "../../../contexts/authentication-context";
import { CompanyContext } from "../../../contexts/company-context";
import { TimeOffOperation } from "../../../serviceoperation/TimeoffOperation";
import { TimeOffReason } from "./timeOff-types";

interface Values {
  reason: string;
  note: string;
  selectedDate: Date[];
}

interface TimeOffRequestModalProps {
  id: number;
  open: boolean;
  onCreate: (values: Values) => void;
  onCancel: () => void;
}

const TimeOffRequestModal: React.FC<TimeOffRequestModalProps> = ({
  id,
  open,
  onCreate,
  onCancel,
}) => {
  const accessToken = useContext(AuthenticationContext)?.accessToken;
  const { selectedCompany } = useContext(CompanyContext);
  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const [showError, setShowError] = useState(false);
  const [timeOffReason, setTimeOffReason] = useState<TimeOffReason[]>([]);

  // Following is set for TimeOff Request Model.
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleTimeOffOk = async (value: Values): Promise<boolean> => {
    let success = false;

    // Format the Start and EndDate.
    const startDate = new Date(value.selectedDate[0]);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(value.selectedDate[1]);
    endDate.setHours(0, 0, 0, 0);

    const timeoffOperation = new TimeOffOperation();
    await timeoffOperation
      .postTimeOffAsync(
        selectedCompany?.corporationId as number,
        userInfo?.employeeCorporation
          ?.filter((x) => x.corporationId === selectedCompany?.corporationId)
          .map((x) => x.employeeId)
          .at(0) ?? "",
        {
          timeOffReasionId: value.reason,
          note: value.note ?? "",
          startDay: startDate,
          endDay: endDate,
          // For now setting this to a Day
          // Once we enable multi days then
          // we can change this.
          startHour: 0,
          startMinute: 0,
          endHour: 23,
          endMinute: 60,
        }
      )
      .then((x) => {
        success =
          x.statusCode != undefined && x.statusCode.toString() === "201";
      });
    return success;
  };

  useEffect(() => {
    const timeZoneOps = new TimeOffOperation();
    timeZoneOps
      .getTimeOffReasonAsync(selectedCompany?.corporationId as number)
      .then((value) => {
        setTimeOffReason(
          value.map((s) => ({
            key: s.timeOffReasonId,
            name: s.name,
          }))
        );
      });
  }, []);

  const [form] = Form.useForm();
  return (
    <Modal
      title="New TimeOff Request Form"
      open={open}
      onOk={() => {
        form.validateFields().then(async (values) => {
          setConfirmLoading(true);
          if (await handleTimeOffOk(values)) {
            form.resetFields();
            onCreate(values);
          } else {
            setShowError(true);
            setConfirmLoading(false);
          }
          setConfirmLoading(false);
        });
      }}
      confirmLoading={confirmLoading}
      onCancel={onCancel}
    >
      <Form
        form={form}
        layout="vertical"
        name="form_in_modal"
        initialValues={{ modifier: "public" }}
      >
        <Form.Item
          name="reason"
          hasFeedback
          label="Reason for Time Off"
          rules={[
            {
              required: true,
              message: "Please select a reason!",
            },
          ]}
          style={{ marginTop: 24 }}
        >
          <Select
            allowClear
            style={{ width: "100%" }}
            placeholder="Please select time off reasons"
            options={timeOffReason.map((tor) => {
              return {
                value: tor.key,
                label: tor.name,
              };
            })}
          />
        </Form.Item>

        <Form.Item
          name="selectedDate"
          label="Time off Dates"
          rules={[
            {
              required: true,
              message: "Please select a date!",
            },
          ]}
        >
          <DatePicker.RangePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="note" label="Notes">
          <Input.TextArea showCount maxLength={255} />
        </Form.Item>
      </Form>
      {showError ? (
        <Alert
          message="There was an error processing your request. Please try again later."
          type="error"
          showIcon
        />
      ) : null}
    </Modal>
  );
};

export default TimeOffRequestModal;
